import React, { useEffect, useState } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Mail, Lock, LogIn, UserPlus, Eye, EyeOff, ArrowLeft } from "lucide-react";
import { GoogleLogin } from "@react-oauth/google";
import BlueprintBackground from "../components/BlueprintBackground";
import BrandLogo from "../components/BrandLogo";
import PageShell from "../components/PageShell";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { useAuth, formatApiError } from "../lib/auth";
import { api } from "../lib/api";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH

export default function LoginPage() {
    const navigate = useNavigate();
    const location = useLocation();
    const { user, login, loginWithGoogleCredential } = useAuth();

    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [showPw, setShowPw] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState("");
    const [processingGoogle, setProcessingGoogle] = useState(false);

    useEffect(() => {
        if (user) navigate("/home", { replace: true });
    }, [user, navigate]);

    const handleGoogleSuccess = async (resp) => {
        const credential = resp?.credential;
        if (!credential) {
            setError("Google sign-in returned no credential. Try again.");
            return;
        }
        setError("");
        setProcessingGoogle(true);
        try {
            await loginWithGoogleCredential(credential);
            toast.success("Signed in with Google");
            navigate("/home", { replace: true });
        } catch (e) {
            setError(formatApiError(e));
        } finally {
            setProcessingGoogle(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!email.trim() || !password) return;
        setError("");
        setSubmitting(true);
        try {
            try {
                const { data } = await api.get("/auth/check-email", {
                    params: { email: email.trim() },
                });
                if (!data.exists) {
                    setError("Username not found");
                    setSubmitting(false);
                    return;
                }
            } catch {
                /* fall through */
            }
            await login(email.trim(), password);
            toast.success("Welcome back!");
            navigate("/home", { replace: true });
        } catch (err) {
            setError(formatApiError(err));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <PageShell testid="login-page">
            <BlueprintBackground />
            <div
                className="login-hero-photo"
                aria-hidden="true"
                style={{ backgroundImage: "url(/images/login-hero.jpg)" }}
            />

            <div className="relative z-10 min-h-full flex flex-col items-center px-4 py-8">
                <Link
                    to="/"
                    data-testid="login-back-home"
                    className="self-start inline-flex items-center gap-1.5 text-slate-300 hover:text-white text-sm font-semibold mb-6"
                >
                    <ArrowLeft className="w-4 h-4" /> Back to Steeltooth
                </Link>

                <div className="w-full max-w-[420px] mx-auto mt-6">
                    <div className="glass rounded-xl p-5 sm:p-7" data-testid="login-panel">
                        <div className="mb-5 text-center">
                            <div className="flex justify-center mb-3">
                                <BrandLogo height={44} />
                            </div>
                            <h1 className="text-xl sm:text-2xl font-bold text-white tracking-tight">
                                Sign in to Steeltooth
                            </h1>
                            <p className="text-xs text-slate-400 mt-1">
                                Precision takeoff for the field and the office.
                            </p>
                        </div>

                        {processingGoogle ? (
                            <div className="flex items-center justify-center py-10 text-slate-300">
                                <Loader2 className="w-5 h-5 animate-spin mr-2" />
                                Finalizing Google sign-in…
                            </div>
                        ) : (
                            <form onSubmit={handleSubmit} className="space-y-3.5" noValidate>
                                <div className="space-y-1.5">
                                    <Label htmlFor="email" className="text-slate-300 text-xs uppercase tracking-wider">Email</Label>
                                    <div className="relative">
                                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500 pointer-events-none" />
                                        <Input
                                            id="email"
                                            type="text"
                                            inputMode="email"
                                            autoComplete="email"
                                            placeholder="you@company.com"
                                            value={email}
                                            onChange={(e) => setEmail(e.target.value)}
                                            data-testid="input-login-email"
                                            className="bg-[#0A1128]/70 border-[#2A3655] text-white placeholder:text-slate-500 focus-visible:ring-[#007AFF] pl-9 h-11"
                                            required
                                        />
                                    </div>
                                </div>
                                <div className="space-y-1.5">
                                    <Label htmlFor="password" className="text-slate-300 text-xs uppercase tracking-wider">Password</Label>
                                    <div className="relative">
                                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500 pointer-events-none" />
                                        <Input
                                            id="password"
                                            type={showPw ? "text" : "password"}
                                            autoComplete="current-password"
                                            value={password}
                                            onChange={(e) => setPassword(e.target.value)}
                                            data-testid="input-login-password"
                                            className="bg-[#0A1128]/70 border-[#2A3655] text-white placeholder:text-slate-500 focus-visible:ring-[#007AFF] pl-9 pr-10 h-11"
                                            required
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowPw((s) => !s)}
                                            aria-label={showPw ? "Hide password" : "Show password"}
                                            className="absolute right-2 top-1/2 -translate-y-1/2 w-7 h-7 rounded-sm flex items-center justify-center text-slate-400 hover:text-white"
                                        >
                                            {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                        </button>
                                    </div>
                                </div>

                                {error && (
                                    <div
                                        data-testid="login-error"
                                        className="text-xs font-semibold text-red-300 bg-red-500/10 border border-red-500/30 rounded-sm px-3 py-2"
                                    >
                                        {error}
                                    </div>
                                )}

                                <Button
                                    type="submit"
                                    disabled={submitting}
                                    data-testid="btn-login"
                                    className="w-full bg-[#007AFF] hover:bg-[#3395FF] text-white font-bold h-11 disabled:opacity-50"
                                >
                                    {submitting ? (
                                        <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Signing in…</>
                                    ) : (
                                        <><LogIn className="w-4 h-4 mr-2" /> Sign In</>
                                    )}
                                </Button>

                                <div className="relative my-2 flex items-center gap-3">
                                    <div className="flex-1 h-px bg-[#2A3655]" />
                                    <span className="text-[10px] uppercase tracking-[0.15em] text-slate-500">or</span>
                                    <div className="flex-1 h-px bg-[#2A3655]" />
                                </div>

                                <div
                                    data-testid="btn-google-signin"
                                    className="w-full flex justify-center overflow-hidden [&>div]:!w-full [&>div]:max-w-full [&>div>div]:!w-full [&>div>div]:max-w-full [&_iframe]:!w-full [&_iframe]:max-w-full"
                                >
                                    <GoogleLogin
                                        onSuccess={handleGoogleSuccess}
                                        onError={() => setError("Google sign-in was cancelled or failed.")}
                                        theme="filled_black"
                                        size="large"
                                        text="continue_with"
                                        shape="rectangular"
                                    />
                                </div>

                                <div className="text-center text-sm text-slate-400 pt-2">
                                    New to Steeltooth?{" "}
                                    <Link
                                        to="/register"
                                        state={{ from: location.pathname }}
                                        data-testid="link-register"
                                        className="text-[#00A3FF] font-semibold hover:text-white inline-flex items-center gap-1"
                                    >
                                        <UserPlus className="w-3.5 h-3.5" />
                                        Create account
                                    </Link>
                                </div>
                            </form>
                        )}
                    </div>

                    <p className="mt-6 text-center text-[11px] uppercase tracking-[0.2em] text-slate-500">
                        Takeoff · Estimate · Deliver
                    </p>
                    <p className="mt-2 text-center text-[11px] text-slate-500">
                        <Link to="/privacy" data-testid="login-privacy-link" className="hover:text-[#00A3FF] underline-offset-2 hover:underline">Privacy policy</Link>
                        <span className="mx-2 text-slate-700">·</span>
                        <Link to="/terms" data-testid="login-terms-link" className="hover:text-[#FFB020] underline-offset-2 hover:underline">Terms of service</Link>
                    </p>
                </div>
            </div>
        </PageShell>
    );
}
